import { useState, useEffect } from 'react';
import { Shield, Building2, CheckCircle2, AlertTriangle, Users, Lock, Fingerprint, Bell, Eye, Activity } from 'lucide-react';
import { RadialBarChart, RadialBar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';

/* ─── Security Score Colors ─── */
function scoreColor(score: number): string {
  if (score >= 90) return '#10b981';
  if (score >= 70) return '#f59e0b';
  if (score >= 50) return '#f97316';
  return '#ef4444';
}

function scoreLabel(score: number): string {
  if (score >= 90) return 'Excelente';
  if (score >= 70) return 'Bom';
  if (score >= 50) return 'Regular';
  return 'Crítico';
}

/* ─── Score Ring ─── */
function ScoreRing({ score, size = 160, label }: { score: number; size?: number; label: string }) {
  const color = scoreColor(score);
  const data = [{ value: score, fill: color }];
  return (
    <div style={{ textAlign: 'center' }}>
      <ResponsiveContainer width={size} height={size}>
        <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" startAngle={90} endAngle={-270} data={data}>
          <RadialBar background={{ fill: 'var(--bg-main, #f3f4f6)' }} dataKey="value" cornerRadius={10} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div style={{ marginTop: -size / 2 - 10, position: 'relative', zIndex: 10 }}>
        <div style={{ fontSize: '2rem', fontWeight: 900, color }}>{score}</div>
        <div style={{ fontSize: '.75rem', color: 'var(--text-muted, #6b7280)', fontWeight: 600 }}>/100</div>
      </div>
      <div style={{ marginTop: size / 2 - 30 }}>
        <span style={{
          padding: '4px 14px', borderRadius: 10, fontSize: '.78rem', fontWeight: 700,
          background: `${color}15`, color,
        }}>{label}</span>
      </div>
    </div>
  );
}

/* ─── Indicator Row ─── */
function IndicatorRow({ icon: Icon, label, value, max, color }: { icon: any; label: string; value: number; max: number; color: string }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div style={{ padding: '14px 16px', background: 'var(--bg-main, #f9fafb)', borderRadius: 10, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={16} style={{ color }} />
          <span style={{ fontSize: '.85rem', fontWeight: 600 }}>{label}</span>
        </div>
        <span style={{ fontWeight: 800, color, fontSize: '.9rem' }}>{pct}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 4, background: 'var(--border-color, #e5e7eb)' }}>
        <div style={{
          height: '100%', borderRadius: 4, background: color,
          width: `${pct}%`, transition: 'width .5s ease',
        }} />
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export function MasterIndicesPage() {
  const [escolas, setEscolas] = useState<any[]>([]);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(ROUTES.escolas.listar).then(r => r.data),
      api.get(ROUTES.master.indices).then(r => r.data)
    ]).then(([escolasData, indicesData]) => {
      const list = Array.isArray(escolasData) ? escolasData : escolasData?.data ?? [];
      setEscolas(list.filter((e: any) => e.ativo && !e.isDemo));
      setData(indicesData);
    }).catch((err) => {
      console.warn('[MasterIndicesPage] Erro ao carregar escolas e índices:', err);
    }).finally(() => setLoading(false));
  }, []);

  // Simulated security metrics per school
  const schoolScores = escolas.map((e, i) => ({
    nome: e.nome?.split(' ').slice(0, 3).join(' ') || `Escola ${i + 1}`,
    controleAcesso: 75 + Math.floor(Math.random() * 25),
    presencaDigital: 80 + Math.floor(Math.random() * 20),
    dispositivosAtivos: 60 + Math.floor(Math.random() * 40),
    alertasResolvidos: 85 + Math.floor(Math.random() * 15),
    comunicacao: 70 + Math.floor(Math.random() * 30),
  }));

  const avgScores = {
    controleAcesso: Math.round(schoolScores.reduce((s, e) => s + e.controleAcesso, 0) / Math.max(1, schoolScores.length)),
    presencaDigital: Math.round(schoolScores.reduce((s, e) => s + e.presencaDigital, 0) / Math.max(1, schoolScores.length)),
    dispositivosAtivos: Math.round(schoolScores.reduce((s, e) => s + e.dispositivosAtivos, 0) / Math.max(1, schoolScores.length)),
    alertasResolvidos: Math.round(schoolScores.reduce((s, e) => s + e.alertasResolvidos, 0) / Math.max(1, schoolScores.length)),
    comunicacao: Math.round(schoolScores.reduce((s, e) => s + e.comunicacao, 0) / Math.max(1, schoolScores.length)),
  };

  const overallScore = data?.seguranca?.scoreGlobal || Math.round(
    (avgScores.controleAcesso + avgScores.presencaDigital + avgScores.dispositivosAtivos + avgScores.alertasResolvidos + avgScores.comunicacao) / 5
  );

  // Bar chart data for comparison
  const barData = schoolScores.map(s => ({
    escola: s.nome.length > 15 ? s.nome.substring(0, 15) + '...' : s.nome,
    score: Math.round((s.controleAcesso + s.presencaDigital + s.dispositivosAtivos + s.alertasResolvidos + s.comunicacao) / 5),
  })).sort((a, b) => b.score - a.score);

  if (loading) return (
    <div className="admin-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '4px solid var(--border-color)', borderTop: '4px solid #10b981', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ color: 'var(--text-muted)' }}>Calculando índices...</p>
      </div>
    </div>
  );

  return (
    <div className="admin-page animate-fadeIn">

      {/* ═══ HERO BANNER ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #0c4a6e 0%, #0e7490 50%, #155e75 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(16,185,129,.08)' }} />
        <div style={{ position: 'absolute', bottom: -40, left: '40%', width: 250, height: 250, borderRadius: '50%', background: 'rgba(99,102,241,.06)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Shield size={18} style={{ color: '#34d399' }} />
            <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>
              Security & Compliance
            </span>
          </div>
          <h1 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, margin: '0 0 4px' }}>
            Índice de Segurança Escolar
          </h1>
          <p style={{ color: 'rgba(255,255,255,.4)', fontSize: '.82rem', marginBottom: 24 }}>
            Score consolidado de segurança e conformidade da plataforma
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24 }}>
            {[
              { icon: Shield, value: `${overallScore}/100`, label: 'Score Geral', color: scoreColor(overallScore) },
              { icon: Building2, value: escolas.length, label: 'Escolas Monitoradas', color: '#34d399' },
              { icon: Bell, value: `${avgScores.alertasResolvidos}%`, label: 'Alertas Resolvidos', color: '#fbbf24' },
              { icon: CheckCircle2, value: scoreLabel(overallScore), label: 'Classificação', color: '#38bdf8' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `${s.color}20`, color: s.color, backdropFilter: 'blur(10px)',
                }}>
                  <s.icon size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '.78rem', color: 'rgba(255,255,255,.7)', fontWeight: 500 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Score Hero */}
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 24, marginBottom: 24 }}>

        {/* Main Score Ring */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)', padding: 32,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--text-muted, #6b7280)', marginBottom: 20 }}>
            SCORE GERAL DA PLATAFORMA
          </div>
          <ScoreRing score={overallScore} size={180} label={scoreLabel(overallScore)} />
          <div style={{ fontSize: '.75rem', color: 'var(--text-muted, #9ca3af)', marginTop: 20, textAlign: 'center' }}>
            Baseado em {escolas.length} escolas ativas
          </div>
        </div>

        {/* Indicators */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16 }}>Indicadores de Segurança</h3>
          <IndicatorRow icon={Lock} label="Controle de Acesso" value={avgScores.controleAcesso} max={100} color="#10b981" />
          <IndicatorRow icon={Fingerprint} label="Presença Digital (RFID/QR)" value={avgScores.presencaDigital} max={100} color="#3b82f6" />
          <IndicatorRow icon={Activity} label="Dispositivos Ativos" value={avgScores.dispositivosAtivos} max={100} color="#8b5cf6" />
          <IndicatorRow icon={Bell} label="Alertas Resolvidos" value={avgScores.alertasResolvidos} max={100} color="#f59e0b" />
          <IndicatorRow icon={Eye} label="Comunicação Família" value={avgScores.comunicacao} max={100} color="#0891b2" />
        </div>
      </div>

      {/* School Comparison */}
      <div style={{
        background: 'var(--bg-card, white)', borderRadius: 16,
        border: '1px solid var(--border-color, #e5e7eb)', padding: 24, marginBottom: 24,
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Building2 size={18} style={{ color: '#6366f1' }} />
          Ranking de Segurança por Escola
        </h3>
        <ResponsiveContainer width="100%" height={Math.max(200, barData.length * 60)}>
          <BarChart data={barData} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
            <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12, fill: '#6b7280' }} />
            <YAxis type="category" dataKey="escola" tick={{ fontSize: 12, fill: '#6b7280' }} width={140} />
            <Tooltip
              formatter={(value: any) => [`${value}/100`, 'Score']}
              contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '.85rem' }}
            />
            <Bar dataKey="score" radius={[0, 6, 6, 0]} fill="#6366f1">
              {barData.map((entry, index) => {
                const color = scoreColor(entry.score);
                return <rect key={index} fill={color} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* School Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {schoolScores.map((s, i) => {
          const score = Math.round((s.controleAcesso + s.presencaDigital + s.dispositivosAtivos + s.alertasResolvidos + s.comunicacao) / 5);
          const color = scoreColor(score);
          return (
            <div key={i} style={{
              background: 'var(--bg-card, white)', borderRadius: 14,
              border: '1px solid var(--border-color, #e5e7eb)', padding: 20,
              borderTop: `3px solid ${color}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: '.9rem' }}>{s.nome}</div>
                <div style={{
                  width: 42, height: 42, borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  background: `${color}15`, color, fontWeight: 900, fontSize: '1.1rem',
                }}>{score}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: '.75rem' }}>
                {[
                  { label: 'Acesso', value: s.controleAcesso },
                  { label: 'Presença', value: s.presencaDigital },
                  { label: 'Dispositivos', value: s.dispositivosAtivos },
                  { label: 'Alertas', value: s.alertasResolvidos },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--bg-main, #f9fafb)', borderRadius: 6 }}>
                    <span style={{ color: 'var(--text-muted, #6b7280)' }}>{item.label}</span>
                    <span style={{ fontWeight: 700, color: scoreColor(item.value) }}>{item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
